import React from 'react';
import styled, { keyframes } from 'styled-components';

type CardProps = {
  children: React.ReactNode;
  footer?: React.ReactNode;
  'data-testid'?: string;
};

export function Card({ children, footer, 'data-testid': dataTestId }: CardProps): JSX.Element {
  return (
    <CardContainer data-testid={dataTestId}>
      <CardWrapper>
        <CardContent>{children}</CardContent>
        {footer}
      </CardWrapper>
    </CardContainer>
  );
}

type LoadingContentProps = {
  text?: string;
  'data-testid'?: string;
};

export function LoadingContent({
  text = 'Loading event details...',
  'data-testid': dataTestId,
}: LoadingContentProps): JSX.Element {
  return (
    <LoadingWrapper data-testid={dataTestId}>
      <Spinner data-testid='irl-event-loading-spinner' />
      <LoadingText>{text}</LoadingText>
    </LoadingWrapper>
  );
}

// Keyframes
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Layout
const CardContainer = styled.div`
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 16px;
`;

const CardWrapper = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 610px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CardContent = styled.div`
  max-width: 450px;
  margin: 0 auto;
  width: 100%;
  padding: 32px;

  @media (min-width: 660px) {
    padding: 32px 0;
  }
`;

export const CardFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.greyBorder};
  padding: 16px 32px;
  width: 100%;
`;

// Loading state
const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px;
`;

const Spinner = styled.div`
  animation: ${spin} 1s linear infinite;
  border-radius: 50%;
  border: 4px solid ${({ theme }) => theme.colors.greyBorder};
  border-top-color: ${({ theme }) => theme.colors.primaryBlue};
  height: 48px;
  margin-bottom: 16px;
  width: 48px;
`;

const LoadingText = styled.p`
  font-size: 16px;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  margin: 0;
`;
